import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Table, Typography, Tooltip, Switch } from "antd";

import LockCloseIcon from "@/icons/LockClosedIcon";
import RefreshIcon from "@/icons/RefreshIcon";
import EyeIcon from "@/icons/EyeIcon";

import ContextMenu from "@/components/containers/ContextMenu";
import CustomLink from "@/components/ui/CustomLink";

import { generateRandomKey } from "@/utils";
import routes from "@/routes";
import { UserContext } from "@/context/UserContext";
import { TEMPLATE_EDIT_ROLES } from "@/constants/task-templates";
import { useColumnConfig } from "@/hooks/useColumnConfig";

const LOCAL_STORAGE_KEY = "taskTemplatesTableColumnParameters";

const COLUMN_LABELS = {
  template_code: "№",
  is_recurring_template: "Повторение",
  name: "Название",
  task_name: "Содержание",
};

const formateData = (data) => {
  if (!data) return;
  return data.map((item) => {
    return {
      ...item,
      key: generateRandomKey(),
    };
  });
};

// Таблица шаблонов задач
// Содержит список с возможностью сортировки
// Тут можно настроить колонки и ContentMenu
const TaskTemplatesTable = ({
  data,
  loading,
  pagination,
  onChange,
  onEditItem,
  onDeleteItem,
  onUpdateVisibleItem,
  unit = null,
  isAdmins = false,
  scroll = { x: "100%" },
  resize = false,
}) => {
  const { user } = useContext(UserContext);

  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    setDataSource(formateData(data));
  }, [data]);

  const baseColumns = useMemo(() => {
    const getHref = (item, unit_uuid) => {
      return isAdmins
        ? routes.administration.taskTemplateDetail(item)
        : unit
          ? routes.unitTaskTemplateDetail(unit_uuid, item)
          : routes.taskTemplateDetail(item);
    };
    return [
      {
        title: <EyeIcon />,
        dataIndex: "is_visible",
        key: "is_visible",
        width: 80,
        align: "center",
        render: (_, record) => (
          <Switch
            disabled={
              !(
                record.roles?.some((role) =>
                  TEMPLATE_EDIT_ROLES.includes(role),
                ) || isAdmins
              )
            }
            checked={record.is_visible}
            onChange={(checked) => {
              onUpdateVisibleItem(record, checked);
            }}
          />
        ),
      },
      {
        title: COLUMN_LABELS.template_code,
        dataIndex: "template_code",
        key: "template_code",
        width: 90,
      },
      {
        title: <RefreshIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
        dataIndex: "is_recurring_template",
        key: "is_recurring_template",
        width: 50,
        align: "center",
        render: (is_recurring_template) =>
          is_recurring_template && (
            <RefreshIcon style={{ fontSize: 14, color: "#BBBBBB" }} />
          ),
      },
      {
        title: COLUMN_LABELS.name,
        dataIndex: "template_title",
        key: "name",
        width: 300,
        render: (template_title, record) => (
          <CustomLink
            to={getHref(record.uuid, record.unit_uuid)}
            color="#303030"
          >
            <Tooltip placement="bottomLeft" title={template_title}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{
                  color: "inherit",
                  lineHeight: 1.4,
                  marginBottom: 0,
                  display: "-webkit-inline-box",
                }}
              >
                {template_title}
              </Typography.Paragraph>
            </Tooltip>
          </CustomLink>
        ),
      },
      {
        title: COLUMN_LABELS.task_name,
        dataIndex: "name",
        key: "task_name",
        width: 300,
        render: (name) => (
          <Tooltip placement="bottomLeft" title={name}>
            <Typography.Paragraph
              ellipsis={{ rows: 2 }}
              style={{
                color: "inherit",
                lineHeight: 1.4,
                marginBottom: 0,
                display: "-webkit-inline-box",
              }}
            >
              {name}
            </Typography.Paragraph>
          </Tooltip>
        ),
      },
      {
        title: "",
        width: 48,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, item) => {
          return !(
            item.roles?.some((role) => TEMPLATE_EDIT_ROLES.includes(role)) ||
            isAdmins
          ) ? (
            <LockCloseIcon style={{ color: "#BBBBBB", fontSize: 18 }} />
          ) : (
            <ContextMenu
              vertical
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
              onEdit={() => onEditItem(item)}
              onDelete={() => onDeleteItem(item)}
            />
          );
        },
      },
    ].filter(Boolean);
  }, [navigate, onEditItem, onDeleteItem, isAdmins, unit, onUpdateVisibleItem]);

  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels: COLUMN_LABELS,
    },
  );

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      rowKey="key"
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      components={resize ? components : undefined}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      // rowClassName={(record) => (record.readOnly ? "row-read-only" : "")}
    />
  );
};

export default TaskTemplatesTable;
